/**
 * Client-side tools for AI agent threads.
 *
 * Each tool lives in its own file and exports a {@link ClientTool}.
 * To add a new client tool:
 *
 * 1. Create a new file in this directory (e.g., `myTool.ts`)
 * 2. Define an input type and implement execute
 * 3. Export a `ClientTool` (directly or via {@link createClientTool})
 * 4. Import and register it in the `TOOL_REGISTRY` map below
 *
 * See `navigate.ts` for a full example with dependencies and parameterized routing.
 */
import { tool as addToCollectionTool } from "./addToCollection";
import { tool as clientCreateAgentTool } from "./clientCreateAgent";
import {
  ToolErrorKind,
  confirmationContentFor,
  createClientTool,
} from "./ClientTools";
import type {
  ClientTool,
  ClientToolConfig,
  ClientToolDeps,
  ClientToolExecutionResult,
  ConfirmationContentProps,
} from "./ClientTools";
import { tool as createCollectionTool } from "./createCollection";
import { tool as createEventsTool } from "./createEvents";
import { tool as createSkillTool } from "./createSkill";
import { tool as navigateTool } from "./navigate";
import {
  addToPanelTool,
  applyLayoutTool,
  openFileTool,
  openPanelTool,
  removeFromPanelTool,
  removePanelTool,
  searchTopicTool,
  seekTool,
  setLogPanelFilterTool,
} from "./visualize";

export { ToolErrorKind, confirmationContentFor, createClientTool };
export type {
  ClientTool,
  ClientToolConfig,
  ClientToolDeps,
  ClientToolExecutionResult,
  ConfirmationContentProps,
};

/**
 * Registry of all client tools, keyed by tool name.
 *
 * Each tool is registered as a {@link ClientTool}. Use
 * {@link createClientTool} to build a module from a typed config.
 *
 * Add new tools here after creating their module file.
 */
const TOOL_REGISTRY: ReadonlyMap<string, ClientTool> = new Map([
  [navigateTool.spec.name, navigateTool],
  [createEventsTool.spec.name, createEventsTool],
  [createCollectionTool.spec.name, createCollectionTool],
  [addToCollectionTool.spec.name, addToCollectionTool],
  [createSkillTool.spec.name, createSkillTool],
  [clientCreateAgentTool.spec.name, clientCreateAgentTool],
  [openPanelTool.spec.name, openPanelTool],
  [addToPanelTool.spec.name, addToPanelTool],
  [removeFromPanelTool.spec.name, removeFromPanelTool],
  [removePanelTool.spec.name, removePanelTool],
  [searchTopicTool.spec.name, searchTopicTool],
  [openFileTool.spec.name, openFileTool],
  [seekTool.spec.name, seekTool],
  [applyLayoutTool.spec.name, applyLayoutTool],
  [setLogPanelFilterTool.spec.name, setLogPanelFilterTool],
]);

/**
 * All registered tool modules, for use by the settings modal and
 * approval resolution logic.
 */
export const CLIENT_TOOL_MODULES: readonly ClientTool[] = [
  ...TOOL_REGISTRY.values(),
];

/**
 * All client-side tool specs, sent to the backend with every thread request
 * so the LLM knows these tools are available.
 */
export const BROWSER_CLIENT_TOOLS = CLIENT_TOOL_MODULES.map(
  (tool) => tool.spec,
);

/**
 * Look up a client tool module by name.
 *
 * Returns undefined for unknown tools.
 *
 * Deliberately the only export that reaches a tool: resolving one is not the
 * same as being allowed to run it. There is no `executeClientTool(name, params)`
 * because such a helper takes no approval mode and asks for no confirmation,
 * and the registry now holds tools — `client_create_collection`,
 * `client_add_to_collection` — whose writes are durable and have no idempotency
 * key. `ClientToolBatchRunner` is the execution path; it applies
 * `resolveApprovalMode`/`needsApproval` to every tool_use it runs.
 */
export function getClientTool(toolName: string): ClientTool | undefined {
  return TOOL_REGISTRY.get(toolName);
}
